import React, { useState } from 'react';
import { Upload, FileText, CheckCircle, Loader2, Zap, AlertCircle, X, Edit, Copy, Save, Cpu, MessageSquare } from 'lucide-react';
import { uploadFile, saveRecord } from '../api/client';
import { AVAILABLE_MODELS, FIELD_CONFIG, getFieldLabel } from '../constants';

export default function UploadPage() {
    const [file, setFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [extractionResult, setExtractionResult] = useState(null);
    const [fullJsonResponse, setFullJsonResponse] = useState(null);
    const [activeTab, setActiveTab] = useState('data');
    const [dataCopied, setDataCopied] = useState(false);
    const [jsonCopied, setJsonCopied] = useState(false);
    const [saveLoading, setSaveLoading] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editedData, setEditedData] = useState({});
    const [selectedModel, setSelectedModel] = useState("gemini-3-flash-preview");

    const handleFileChange = (event) => {
        const selectedFile = event.target.files?.[0] || null;
        setFile(selectedFile);
        setError("");
        setExtractionResult(null);
        setFullJsonResponse(null);
        setSaveSuccess(false);
    };

    const handleUpload = async () => {
        if (!file) {
            setError("Veuillez sélectionner un fichier avant de lancer l'extraction.");
            return;
        }

        setLoading(true);
        setError("");
        setExtractionResult(null);
        setFullJsonResponse(null);
        setActiveTab('data');
        setSaveSuccess(false);

        try {
            const selectedModelConfig = AVAILABLE_MODELS.find(model => model.value === selectedModel);
            const result = await uploadFile(file, selectedModelConfig.provider, selectedModelConfig.value);

            setFullJsonResponse(result);
            setExtractionResult(result.data);
            setEditedData(result.data);
            setIsEditing(false);

        } catch (err) {
            console.error("Upload error:", err);
            setError(err.message || "Une erreur est survenue lors de l'extraction.");
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        if (!fullJsonResponse) return;

        setSaveLoading(true);
        setSaveSuccess(false);
        setError("");

        try {
            const dataToSave = {};

            // Reverse mapping to backend fields
            const reverseLabelMapping = Object.entries(FIELD_CONFIG).reduce((acc, [key, config]) => {
                // FIELD_CONFIG keys are like "NOS REFERENCES"
                // But editedData keys are formatted labels "Nos Références" ? 
                // Wait, App.js logic was:
                // result.data contained keys which were mapped from backend fields to Frontend Labels.
                // french_label_mapping keys were backend fields, values were Frontend Labels.

                // App.js lines 238-271 hardcoded the reverse mapping. 
                // Ideally we derive it from FIELD_CONFIG if compatible.
                // FIELD_CONFIG: "NOS REFERENCES": { label: "Nos Références", backendField: "nos_references" }
                // But french_label_mapping in backend was: "nos_references" -> "NOS REFERENCES"

                // Wait, let's look at Backend records.py (Step 57)
                /*
                french_label_mapping = {
                   "nos_references": "NOS REFERENCES",
                   ...
                }
                */
                // So backend returns "NOS REFERENCES".
                // Frontend receive "NOS REFERENCES".

                // FIELD_CONFIG in constants.js (Step 74)
                /*
                "NOS REFERENCES": { label: "Nos Références", ... }
                */

                // App.js used `getFieldLabel(key)` to display. 
                // `getFieldLabel` takes "NOS REFERENCES" and returns "Nos Références".

                // App.js handleSave logic (Step 30, lines 238+):
                /*
                const reverseLabelMapping = {
                  "NOS REFERENCES": "nos_references",
                  ...
                }
                */
                // This maps the DATA KEY (which is "NOS REFERENCES") to BACKEND FIELD ("nos_references").
                // Yes, so I can use FIELD_CONFIG keys directly.

                // FIELD_CONFIG keys ARE the "NOS REFERENCES" strings.
                // Values have backendField.
                // So mapping is key -> value.backendField.

                // But note: FIELD_CONFIG might not cover ALL fields if `french_label_mapping` in backend has more.
                // The manual mapping in App.js matches Backend mapping.

                // Let's rely on FIELD_CONFIG mostly, but check for mismatches.
                // Or recreate the map like App.js did.

                // I'll assume FIELD_CONFIG keys match the keys in `editedData`.

                return acc;
            }, {});

            // Let's implement robust mapping
            const backendFieldsMap = {};
            Object.entries(FIELD_CONFIG).forEach(([key, config]) => {
                backendFieldsMap[key] = config.backendField;
            });

            // Add specific manual mappings if missing in FIELD_CONFIG (App.js had explicit map)
            // Check App.js vs FIELD_CONFIG.
            // App.js had "Date d'examen": "date_examen" etc. 
            // Backend keys: "Date d'examen". 
            // FIELD_CONFIG doesn't list "Date d'examen" as a key? 
            // Step 74 FIELD_CONFIG:
            /*
            "NOS REFERENCES": ...
            "DATE DE MISSION": ...
            "DATE ACCIDENT": ...
            */
            // App.js keys for `french_label_mapping`: "Date d'examen", "Véh. Expertisé"...
            // My FIELD_CONFIG in constants.js (Step 74) seems to have ONLY SOME fields?
            // Ah, Step 74: 
            /*
            export const FIELD_CONFIG = {
                "NOS REFERENCES": ...,
                "DATE DE MISSION": ...,
                "MANDANT": ...,
                "AGENCE": ...,
                "DATE ACCIDENT": ...,
                "ASSURE": ...,
                "TELEPHONE ASSURE": ...,
                "IMMATRICULATION": ...,
                "EXPERTISE TYPE": ...,
                "ADRESSE EXPERTISE": ...,
            };
            */
            // This list seems SHORTER than `french_label_mapping` in Backend (Step 57).
            // Backend has "Date d'examen", "Véh. Expertisé", "VA:", "Lieu d'examen", "Observation"...
            // FIELD_CONFIG in App.js (Step 30) was also short! Line 78-89.
            // Yet App.js handleSave (Line 238) had a HUGE map.
            // So `FIELD_CONFIG` was just for display icons/labels maybe?
            // App.js `getFieldLabel` (Line 106):
            /*
            const getFieldLabel = (key) => {
              const config = Object.values(FIELD_CONFIG).find(
                (field) => field.backendField === key.toLowerCase().replace(/ /g, '_')
              );
              // ...
            */
            // This logic is fuzzy. 

            // I should probably copy the ROBUST reverse mapping from App.js to be safe.

            const manualReverseMap = {
                "NOS REFERENCES": "nos_references",
                "DATE DE MISSION": "date_mission",
                "MANDANT": "mandant",
                "AGENCE": "agence",
                "Date Accident": "date_accident",
                "Assuré(e)": "assure",
                "Tiers": "tiers",
                "Contrat": "contrat",
                "Contrat 2": "contrat_2",
                "N° Dossier": "numero_dossier",
                "CIE Adv.": "cie_adv",
                "Immatriculation": "immatriculation",
                "Immatriculation Véh. Tiers": "immatriculation_tiers",
                "Date d'examen": "date_examen",
                "Véh. Expertisé": "vehicule_expertise",
                "VA:": "va",
                "Lieu d'examen": "lieu_examen",
                "Observation": "observation",
                "Marque": "marque",
                "Genre": "genre",
                "N° Série": "numero_serie",
                "Type": "type_vehicule",
                "Couleur": "couleur",
                "Date 1° MC": "date_premiere_mc",
                "Puissance": "puissance",
                "Etat Véh.": "etat_vehicule",
                "Circonstance": "circonstance",
                "Index Km": "index_km",
                "Energie": "energie",
                "VN": "vn",
                "VV": "vv",
                "NATURE DES CHOC ET DES DEGATS": "nature_chocs_degats"
            };

            Object.entries(editedData).forEach(([frenchLabel, value]) => {
                const backendField = manualReverseMap[frenchLabel] || backendFieldsMap[frenchLabel];
                if (backendField) {
                    dataToSave[backendField] = value;
                }
            });

            const payload = {
                record_id: fullJsonResponse.record_id,
                transcription: fullJsonResponse.transcription,
                ...dataToSave
            };

            await saveRecord(payload);

            setSaveSuccess(true);
            setExtractionResult(null);
            setFullJsonResponse(null);
            setFile(null);
            setIsEditing(false);
            setEditedData({});

        } catch (err) {
            console.error("Save error:", err);
            setError(err.message || "Une erreur est survenue lors de l'enregistrement.");
        } finally {
            setSaveLoading(false);
        }
    };

    const handleFieldChange = (key, value) => {
        setEditedData(prev => ({
            ...prev,
            [key]: value
        }));
    };

    const handleCancelEdit = () => {
        setEditedData(extractionResult);
        setIsEditing(false);
    };

    const copyToClipboard = (text, type) => {
        if (!text) return;
        navigator.clipboard.writeText(text).then(() => {
            if (type === 'data') {
                setDataCopied(true);
                setTimeout(() => setDataCopied(false), 2000);
            } else {
                setJsonCopied(true);
                setTimeout(() => setJsonCopied(false), 2000);
            }
        });
    };

    const copyDataToClipboard = () => {
        if (!editedData) return;
        const textToCopy = Object.entries(editedData)
            .map(([key, value]) => `${getFieldLabel(key)}: ${value}`)
            .join('\n');
        copyToClipboard(textToCopy, 'data');
    };

    const copyJsonToClipboard = () => {
        if (!fullJsonResponse) return;
        const textToCopy = JSON.stringify(fullJsonResponse, null, 2);
        copyToClipboard(textToCopy, 'json');
    };

    return (
        <div className="p-6 bg-gray-50 min-h-[calc(100vh-64px)]">
            <div className="max-w-4xl mx-auto">
                <h1 className="text-3xl font-bold mb-8 text-gray-800 flex items-center gap-3">
                    <Upload className="w-7 h-7 text-blue-600" />
                    Extraction et Enregistrement de Dossiers
                </h1>

                {/* Upload Card */}
                <div className="bg-white shadow-xl border border-gray-200 rounded-xl p-8 mb-8">
                    {/* Model Selection */}
                    <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                        <label className="block text-sm font-medium text-blue-700 mb-3 flex items-center gap-2">
                            <Cpu className="w-4 h-4" />
                            Sélection du Modèle IA
                        </label>
                        <div className="space-y-3">
                            {AVAILABLE_MODELS.map((model) => (
                                <div key={model.value} className="flex items-center">
                                    <input
                                        type="radio"
                                        id={model.value}
                                        name="model"
                                        value={model.value}
                                        checked={selectedModel === model.value}
                                        onChange={(e) => setSelectedModel(e.target.value)}
                                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                                    />
                                    <label htmlFor={model.value} className="ml-3 block text-sm font-medium text-gray-700">
                                        {model.label}
                                    </label>
                                </div>
                            ))}
                        </div>
                        <div className="mt-2 text-xs text-gray-500">
                            Modèle sélectionné: <span className="font-medium">
                                {AVAILABLE_MODELS.find(m => m.value === selectedModel)?.label}
                            </span>
                        </div>
                    </div>

                    <label
                        htmlFor="file-upload"
                        className={`block w-full text-center py-6 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${file ? 'border-green-400 bg-green-50' : 'border-blue-300 hover:border-blue-500 bg-gray-50'
                            }`}
                    >
                        {file ? (
                            <div className="text-green-700 font-semibold flex items-center justify-center gap-2">
                                <CheckCircle className="w-5 h-5" /> Fichier sélectionné: {file.name}
                            </div>
                        ) : (
                            <div className="text-blue-600 font-semibold">
                                <FileText className="w-6 h-6 mx-auto mb-2" />
                                Cliquez pour choisir un fichier d'expertise (PDF, JPG, PNG)
                            </div>
                        )}
                        <input id="file-upload" type="file" onChange={handleFileChange} className="hidden" accept=".pdf,.jpg,.jpeg,.png" />
                    </label>

                    <button
                        onClick={handleUpload}
                        disabled={!file || loading}
                        className="w-full mt-6 flex items-center justify-center gap-3 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-semibold transition-colors shadow-md hover:shadow-lg text-lg"
                    >
                        {loading ? (
                            <>
                                <Loader2 className="animate-spin w-5 h-5" />
                                Extraction en cours...
                            </>
                        ) : (
                            <>
                                <Zap className="w-5 h-5" />
                                Lancer l'Extraction
                            </>
                        )}
                    </button>
                </div>

                {/* Error Display */}
                {error && (
                    <div className="p-4 mb-6 bg-red-100 border-l-4 border-red-500 text-red-700 rounded-lg font-medium flex items-center gap-2">
                        <AlertCircle className="w-5 h-5 flex-shrink-0" />
                        <span>Erreur: {error}</span>
                    </div>
                )}

                {/* Results Card */}
                {extractionResult && (
                    <div className="bg-white shadow-xl border border-gray-200 rounded-xl p-6">
                        <div className="flex items-center justify-between border-b pb-4 mb-4">
                            <h2 className="text-2xl font-bold text-green-700 flex items-center gap-2">
                                <CheckCircle className="w-6 h-6" /> Extraction Réussie
                            </h2>
                            <div className="flex items-center gap-2">
                                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                                    {AVAILABLE_MODELS.find(m => m.value === selectedModel)?.provider === 'gemini' ? 'Gemini' : 'Qwen'}
                                </span>
                                <button
                                    onClick={() => {
                                        setExtractionResult(null);
                                        setIsEditing(false);
                                        setEditedData({});
                                    }}
                                    className="text-gray-500 hover:text-red-600 transition-colors p-1 rounded-full hover:bg-red-50"
                                >
                                    <X className="w-6 h-6" />
                                </button>
                            </div>
                        </div>

                        {/* Tabs */}
                        <div className="flex border-b border-gray-200 mb-6">
                            <button
                                onClick={() => setActiveTab('data')}
                                className={`px-4 py-2 font-medium text-sm transition-colors ${activeTab === 'data' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500 hover:text-gray-700'
                                    }`}
                            >
                                Données Structurées
                            </button>
                            <button
                                onClick={() => setActiveTab('json')}
                                className={`px-4 py-2 font-medium text-sm transition-colors ${activeTab === 'json' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500 hover:text-gray-700'
                                    }`}
                            >
                                Réponse JSON
                            </button>
                        </div>

                        {/* Tab Content */}
                        <div>
                            {/* Tab: Structured Data */}
                            {activeTab === 'data' && (
                                <div>
                                    <div className="flex justify-between items-center mb-4">
                                        <h2 className="text-xl font-bold text-gray-800">Champs Extraits</h2>
                                        <div className="flex gap-2">
                                            {!isEditing ? (
                                                <>
                                                    <button
                                                        onClick={() => setIsEditing(true)}
                                                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium transition-colors"
                                                    >
                                                        <Edit className="w-4 h-4" /> Modifier
                                                    </button>
                                                    <button
                                                        onClick={copyDataToClipboard}
                                                        className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 text-sm font-medium"
                                                    >
                                                        <Copy className="w-4 h-4" /> {dataCopied ? "Copié!" : "Copier"}
                                                    </button>
                                                </>
                                            ) : (
                                                <button
                                                    onClick={handleCancelEdit}
                                                    className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 text-sm font-medium"
                                                >
                                                    <X className="w-4 h-4" /> Annuler
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        {Object.entries(editedData)
                                            .filter(([key]) => key !== 'transcription')
                                            .map(([key, value]) => {
                                                const label = getFieldLabel(key);
                                                const Icon = FIELD_CONFIG[key]?.icon || MessageSquare;

                                                return (
                                                    <div key={key} className={`p-4 rounded-lg border shadow-sm ${isEditing ? 'bg-white border-blue-300' : 'bg-blue-50 border-blue-200'}`}>
                                                        <div className="flex items-center text-sm font-bold text-blue-700 mb-1">
                                                            <Icon className="w-4 h-4 mr-2" /> {label}
                                                        </div>
                                                        {isEditing ? (
                                                            <input
                                                                type="text"
                                                                value={value || ''}
                                                                onChange={(e) => handleFieldChange(key, e.target.value)}
                                                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-800 font-medium text-base"
                                                            />
                                                        ) : (
                                                            <p className="text-gray-800 font-medium text-base break-words">{value || 'N/A'}</p>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                    </div>

                                    {fullJsonResponse?.transcription && (
                                        <div className="mt-6">
                                            <h3 className="text-lg font-bold text-gray-800 mb-2 flex items-center gap-2">
                                                <MessageSquare className="w-5 h-5 text-gray-600" /> Transcription brute
                                            </h3>
                                            <div className="bg-gray-100 p-4 rounded-lg text-sm text-gray-700 max-h-48 overflow-y-auto border border-gray-200">
                                                <p className="whitespace-pre-wrap">{fullJsonResponse.transcription}</p>
                                            </div>
                                        </div>
                                    )}

                                    <div className="mt-8 text-center">
                                        {saveSuccess ? (
                                            <div className="inline-flex items-center gap-2 px-6 py-3 bg-green-500 text-white rounded-lg font-semibold shadow-lg">
                                                <CheckCircle className="w-5 h-5" /> Dossier enregistré avec succès !
                                            </div>
                                        ) : (
                                            <button
                                                onClick={handleSave}
                                                disabled={saveLoading || !fullJsonResponse}
                                                className="inline-flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed font-semibold transition-colors shadow-lg text-lg"
                                            >
                                                {saveLoading ? (
                                                    <>
                                                        <Loader2 className="animate-spin w-5 h-5" /> Enregistrement...
                                                    </>
                                                ) : (
                                                    <>
                                                        <Save className="w-5 h-5" /> Enregistrer le Dossier
                                                    </>
                                                )}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Tab: Raw JSON */}
                            {activeTab === 'json' && (
                                <div>
                                    <div className="flex justify-between items-center mb-4">
                                        <h2 className="text-xl font-bold text-gray-800">Réponse JSON Complète</h2>
                                        {fullJsonResponse && (
                                            <button onClick={copyJsonToClipboard} className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 text-sm font-medium">
                                                <Copy className="w-4 h-4" /> {jsonCopied ? "Copié!" : "Copier JSON"}
                                            </button>
                                        )}
                                    </div>
                                    <div className="bg-gray-900 text-green-400 p-4 rounded-lg text-sm overflow-auto max-h-96 shadow-lg">
                                        <pre className="font-mono">
                                            {JSON.stringify(fullJsonResponse, null, 2)}
                                        </pre>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
